// src/services/dataAdapters/CSVDataAdapter.js
// DataAdapter implementation backed by an in-memory parsed CSV dataset.
//
// Usage (from CSV text):
//   const adapter = new CSVDataAdapter({ csvText: '...', label: 'file.csv' });
//
// Usage (from pre-parsed data, e.g. from a stored validation dataset):
//   const adapter = new CSVDataAdapter({ columns: [...], rows: [...] });
//
// The adapter exposes a single logical table named 'data'.
// Filter, limit, and column projection are all supported.

import DataAdapter from './DataAdapter';

class CSVDataAdapter extends DataAdapter {
  /**
   * @param {{
   *   csvText?: string,   — raw CSV text to parse
   *   columns?: string[], — pre-parsed column names (used when csvText is absent)
   *   rows?:    object[], — pre-parsed rows         (used when csvText is absent)
   *   label?:   string,   — human-readable label
   * }} config
   *
   * If csvText is provided it is parsed; otherwise columns/rows are used directly.
   */
  constructor(config = {}) {
    super();
    if (config.csvText) {
      const parsed = CSVDataAdapter._parseCSV(config.csvText);
      this._columns = parsed ? parsed.columns : [];
      this._rows    = parsed ? parsed.rows    : [];
    } else {
      this._columns = config.columns || [];
      this._rows    = config.rows    || [];
    }
    this._label = config.label || 'CSV';
  }

  // ── type ──────────────────────────────────────────────────────────────────

  get type() {
    return 'csv';
  }

  // ── ping ──────────────────────────────────────────────────────────────────

  async ping() {
    return { success: true, latencyMs: 0 };
  }

  // ── listTables / listColumns ──────────────────────────────────────────────

  /** A CSV file is treated as a single table called 'data'. */
  async listTables() {
    return ['data'];
  }

  /**
   * Returns column definitions derived from the CSV header row.
   * All columns are typed as 'text' since CSV has no native type information.
   * The table parameter is ignored — a CSV file has only one table.
   */
  // eslint-disable-next-line no-unused-vars
  async listColumns(table) {
    return this._columns.map(name => ({ name, type: 'text', nullable: true }));
  }

  // ── query ─────────────────────────────────────────────────────────────────

  /**
   * Query the CSV data.
   * The table parameter is ignored (CSV has only one table: 'data').
   * Supports exact-match filter, column projection, and row limit.
   */
  // eslint-disable-next-line no-unused-vars
  async query({ table, columns, filter, limit } = {}) {
    let rows = [...this._rows];

    if (filter && Object.keys(filter).length > 0) {
      rows = rows.filter(row =>
        Object.entries(filter).every(([key, val]) => row[key] === val)
      );
    }

    if (limit && limit > 0) {
      rows = rows.slice(0, limit);
    }

    if (columns && columns.length > 0) {
      rows = rows.map(row => {
        const out = {};
        for (const col of columns) {
          if (Object.prototype.hasOwnProperty.call(row, col)) {
            out[col] = row[col];
          }
        }
        return out;
      });
    }

    return rows;
  }

  // ── count ─────────────────────────────────────────────────────────────────

  // eslint-disable-next-line no-unused-vars
  async count({ table, filter } = {}) {
    if (!filter || Object.keys(filter).length === 0) {
      return this._rows.length;
    }
    return this._rows.filter(row =>
      Object.entries(filter).every(([key, val]) => row[key] === val)
    ).length;
  }

  // ── schemaCapabilities ────────────────────────────────────────────────────

  async schemaCapabilities() {
    return {
      canListTables:      true,
      canListColumns:     true,
      canIntrospectTypes: false, // all columns are typed as 'text' in CSV
      supportsFilter:     true,
    };
  }

  // ── Static CSV parsing helpers ─────────────────────────────────────────────

  /**
   * Parse CSV text into { columns, rows }.
   * Returns null if the text has fewer than two non-empty lines.
   * Auto-detects comma, semicolon, and tab delimiters.
   *
   * @param {string} csvText
   * @returns {{ columns: string[], rows: object[] } | null}
   */
  static _parseCSV(csvText) {
    const lines = csvText.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return null;

    // Auto-detect delimiter from first line
    const firstLine      = lines[0];
    const semicolonCount = (firstLine.match(/;/g) || []).length;
    const commaCount     = (firstLine.match(/,/g) || []).length;
    const tabCount       = (firstLine.match(/\t/g) || []).length;
    const delimiter =
      tabCount > commaCount && tabCount > semicolonCount ? '\t' :
      semicolonCount > commaCount ? ';' : ',';

    const columns = CSVDataAdapter._splitCSVLine(lines[0], delimiter);
    const rows    = [];

    for (let i = 1; i < lines.length; i++) {
      const values = CSVDataAdapter._splitCSVLine(lines[i], delimiter);
      if (values.length === 0) continue;

      const row = {};
      columns.forEach((col, idx) => {
        row[col] = values[idx] !== undefined ? values[idx].trim() : '';
      });
      rows.push(row);
    }

    return { columns, rows };
  }

  /**
   * RFC-4180-compliant CSV line splitter (handles quoted fields with embedded
   * commas/newlines and escaped double quotes).
   *
   * @param {string} line
   * @param {string} delimiter
   * @returns {string[]}
   */
  static _splitCSVLine(line, delimiter) {
    const result = [];
    let current  = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === delimiter && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  }
}

export default CSVDataAdapter;
